import { useParams, Link, Navigate } from "react-router-dom";
import {
  Plane, Building2, CalendarClock, AlertTriangle, ArrowRight, ArrowLeft,
  Clock, FileText, CheckCircle2, Scale, DollarSign,
} from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { SLUG_MAP } from "@/data/services";

const ICONS = { Plane, Building2, CalendarClock, AlertTriangle };

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SLUG_MAP[slug];
  if (!s) return <Navigate to="/services" replace />;
  const Icon = ICONS[s.icon] || Plane;

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={s.hero} alt={s.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-navy/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 text-white">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-6"><ArrowLeft className="w-4 h-4" /> All services</Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-red flex items-center justify-center"><Icon className="w-6 h-6" /></div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-gold"><Clock className="w-3.5 h-3.5" /> {s.processing}</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-serif text-5xl md:text-6xl tracking-tight max-w-3xl">{s.title}</motion.h1>
          <p className="mt-5 text-slate-200 text-lg max-w-2xl leading-relaxed">{s.summary}</p>
          <Link to={`/apply/${s.slug}`} data-testid="detail-apply-btn"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow">
            Apply for this service <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          <Reveal>
            <h2 className="font-serif text-3xl text-brand-navy tracking-tight flex items-center gap-3"><CheckCircle2 className="w-6 h-6 text-brand-red" /> What's included</h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {s.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-brand-red shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <h2 className="font-serif text-3xl text-brand-navy tracking-tight flex items-center gap-3"><FileText className="w-6 h-6 text-brand-red" /> Requirements</h2>
            <ul className="mt-6 space-y-3">
              {s.requirements.map((r) => (
                <li key={r} className="flex items-start gap-3 text-slate-700"><span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 shrink-0" /> {r}</li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <h2 className="font-serif text-3xl text-brand-navy tracking-tight">How the process works</h2>
            <div className="mt-6 space-y-4">
              {s.process.map((p, i) => (
                <div key={p.t} className="flex gap-4 bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-soft transition-shadow">
                  <div className="w-9 h-9 rounded-full bg-brand-navy text-white flex items-center justify-center font-semibold text-sm shrink-0">{i + 1}</div>
                  <div><h3 className="font-semibold text-brand-navy">{p.t}</h3><p className="text-sm text-slate-600 mt-1">{p.d}</p></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-28 space-y-6">
            <Reveal>
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-soft">
                <h3 className="font-semibold text-brand-navy flex items-center gap-2"><DollarSign className="w-5 h-5 text-brand-red" /> Fees</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.fee_note}</p>
                <Link to={`/apply/${s.slug}`} className="mt-5 block text-center px-5 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors">Get a quotation</Link>
              </div>
            </Reveal>
            <Reveal>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <h3 className="font-semibold text-brand-navy flex items-center gap-2"><Scale className="w-5 h-5 text-brand-red" /> Legal basis</h3>
                <ul className="mt-3 space-y-2.5">
                  {s.regulations.map((r) => (
                    <li key={r} className="text-xs text-slate-600 leading-relaxed border-l-2 border-brand-gold pl-3">{r}</li>
                  ))}
                </ul>
                <p className="mt-4 text-[11px] text-slate-400">Information provided for guidance and prepared in line with Vietnamese regulations. ImmiLaw.VN is a private consultancy, not a government body.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
